import Rails from "@rails/ujs";
import { Controller } from 'stimulus';

export default class extends Controller {
  static get targets() {
    return [
      'generalStep',
      'generalStepForm',
      'phoneExt',
      'phoneNumber',
      'inputFirstName',
      'inputLastName',
      'inputCompanyName',
      'inputEmail',
      'checkboxTosAgreed',
      'buttonNext',
      'detailsStep',
      'detailsStepForm',
      'buttonBack',
      'buttonSend',
      'successStep',
      'errorStep',
      'autofocus',
    ];
  }

  connect() {
    this.formInputs = [
      this.inputFirstNameTarget,
      this.inputLastNameTarget,
      this.inputCompanyNameTarget,
      this.inputEmailTarget,
      this.phoneNumberTarget,
    ];

    if (this.hasPhoneExtTarget && this.hasPhoneNumberTarget) {
      if (this.phoneNumberTarget.value === '') {
        this.phoneNumberTarget.value = `+${this.phoneExtTarget.value}`;
      }
    }

    this.generalStepFormTarget.addEventListener('focus', this.onGeneralStepFormFocus, true)
    this.generalStepFormTarget.addEventListener('ajax:error', this.onGeneralStepFormError, true)
    this.generalStepFormTarget.addEventListener('ajax:success', this.onGeneralStepFormSuccess, true)
    this.detailsStepFormTarget.addEventListener('ajax:error', this.onDetailsStepFormError, true)
    this.detailsStepFormTarget.addEventListener('ajax:success', this.onDetailsStepFormSuccess, true)
    this.buttonNextTarget.addEventListener('click', this.onClickNext, true)
    this.buttonBackTarget.addEventListener('click', this.onClickBack, true)
    this.buttonSendTarget.addEventListener('click', this.onClickSend, true)
    this.phoneExtTarget.addEventListener('change', this.onChangePhoneExt, true)
  }

  disconnect() {
    this.generalStepFormTarget.removeEventListener('focus', this.onGeneralStepFormFocus, true)
    this.generalStepFormTarget.removeEventListener('ajax:error', this.onGeneralStepFormError, true)
    this.generalStepFormTarget.removeEventListener('ajax:success', this.onGeneralStepFormSuccess, true)
    this.detailsStepFormTarget.removeEventListener('ajax:error', this.onDetailsStepFormError, true)
    this.detailsStepFormTarget.removeEventListener('ajax:success', this.onDetailsStepFormSuccess, true)
    this.buttonNextTarget.removeEventListener('click', this.onClickNext, true)
    this.buttonBackTarget.removeEventListener('click', this.onClickBack, true)
    this.buttonSendTarget.removeEventListener('click', this.onClickSend, true)
    this.phoneExtTarget.removeEventListener('change', this.onChangePhoneExt, true)
  }

  fieldsMap(id) {
    return {
      first_name: this.inputFirstNameTarget,
      last_name: this.inputLastNameTarget,
      company: this.inputCompanyNameTarget,
      email: this.inputEmailTarget,
      phone: this.phoneNumberTarget,
      tos_agreed: this.checkboxTosAgreedTarget,
    }[id];
  }

  showStep(stepName) {
    this.generalStepTarget.style.display = stepName === 'general' ? '' : 'none';
    this.detailsStepTarget.style.display = stepName === 'details' ? '' : 'none';
    this.successStepTarget.style.display = stepName === 'success' ? '' : 'none';
    this.errorStepTarget.style.display = stepName === 'error' ? '' : 'none';
  }

  openModal() {
    this.showStep('general')

    if (this.hasAutofocusTarget) {
      this.inputFirstNameTarget.focus();
    }
  }

  onClickNext = async (_event) => {
    const form = this.generalStepFormTarget;
    const token = await this.getRecaptchaToken();
    if (token) {
      let input = form.querySelector('input[name="recaptcha_token"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'recaptcha_token';
        form.appendChild(input);
      }
      input.value = token;
    }
    Rails.fire(form, 'submit');
  }

  getRecaptchaToken() {
    const siteKey = document.querySelector('meta[name="recaptcha-site-key"]')?.content;
    if (!siteKey || typeof grecaptcha === 'undefined') return Promise.resolve(null);
    const tokenPromise = new Promise((resolve) => {
      if (typeof grecaptcha.ready !== 'function') { resolve(null); return; }
      grecaptcha.ready(function() {
        if (typeof grecaptcha.execute !== 'function') { resolve(null); return; }
        grecaptcha.execute(siteKey, { action: 'create_briefing' }).then(resolve).catch(() => resolve(null));
      });
    });
    const timeout = new Promise((r) => setTimeout(() => r(null), 3000));
    return Promise.race([tokenPromise, timeout]);
  }

  onGeneralStepFormSuccess = (event) => {
    const { detail } = event;
    const id = detail[0].id;

    this.showStep('details')

    this.detailsStepFormTarget.action = `/briefings/${id}.json`;
  }

  onGeneralStepFormError = (event) => {
    const { detail } = event;
    const payload = detail?.[0];
    const errors = payload?.errors && typeof payload.errors === 'object' ? payload.errors : { base: 'Something went wrong. Please try again.' };

    this.element.querySelectorAll('.bs-form__field-error').forEach((errorField) => {
      errorField.remove();
    });

    Object.keys(errors).forEach((id) => {
      const message = errors[id];
      const inputEl = this.fieldsMap(id);
      const fieldError = `<div class="bs-form__field-error">${message}</div>`;

      if (inputEl) {
        inputEl.classList.toggle('invalid', true);
        inputEl.parentElement.insertAdjacentHTML('beforeend', fieldError);
      } else {
        const actionsEl = this.generalStepTarget.querySelector('.bs-briefing__actions');
        if (actionsEl) actionsEl.insertAdjacentHTML('beforebegin', fieldError);
      }
    });
  }

  onGeneralStepFormFocus = (event) => {
    this.removeExistingErrorMessage(event.target)
  }

  onClickBack = () => {
    this.showStep('general')
  }

  onClickSend = (_event) => {
    Rails.fire(this.detailsStepFormTarget, 'submit');

    if (dataLayer) {
      dataLayer.push({ event: 'InquirySubmit' });
    }
  }

  onDetailsStepFormSuccess = () => {
    this.formInputs.forEach((formInput) => {
      if (formInput) {
        formInput.value = '';
      }
    });

    this.showStep('success');
  }

  onDetailsStepFormError = (_event) => {
    this.showStep('error')
  }

  onChangePhoneExt = (event) => {
    if (this.hasPhoneNumberTarget) {
      this.phoneNumberTarget.value = `+${event.target.value}`;
    }
  }

  removeExistingErrorMessage (field) {
    const fieldContainer = field.closest('.bs-form__field')
    if(!fieldContainer)
      return;

    const existingErrorMessageElement = fieldContainer.querySelector('.bs-form__field-error')
    if (existingErrorMessageElement)
      existingErrorMessageElement.parentNode.removeChild(existingErrorMessageElement)
  }
}
